// Checkbox — boolean control per COMPONENTS §Catalog + §Per-primitive WCAG.
//
// A real native <input type="checkbox"> drives semantics + keyboard (Space) +
// focus; it's visually hidden (`sr-only peer`) behind a token-styled box so the
// browser owns the a11y contract and we own the look. The whole control is
// wrapped in a <label>, so clicking the box OR the label text toggles — no
// hand-rolled htmlFor wiring, no role="checkbox" button (dev's weaker pattern).
//
// Controlled: `checked` + `onChange(checked: boolean)`. Validation is
// form-state-level (RHF + Zod resolver per ADR-008) — wire via Controller; the
// `error` prop just renders the visual message.

import { useId, type ReactNode } from 'react';
import { icons } from './icons.js';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Inline label (caller-supplied, i18n'd). Clicking it toggles. */
  label?: ReactNode;
  /** Accessible name when there is no visible `label` (e.g. a table row-select
   * checkbox). Exactly one of `label` / `ariaLabel` must be provided. */
  ariaLabel?: string;
  /** Secondary line under the label. */
  description?: ReactNode;
  disabled?: boolean;
  /** Error message displayed below; sets aria-invalid + aria-describedby. */
  error?: string;
  id?: string;
  name?: string;
  className?: string;
}

export function Checkbox({
  checked,
  onChange,
  label,
  ariaLabel,
  description,
  disabled = false,
  error,
  id: idProp,
  name,
  className = '',
}: CheckboxProps) {
  const reactId = useId();
  const id = idProp ?? reactId;
  const errorId = `${id}-error`;
  const descId = `${id}-desc`;
  const describedBy = [error ? errorId : null, description ? descId : null].filter(Boolean).join(' ');

  return (
    // flex (not block) so the inline-flex <label> is a flex item, not inline content:
    // a block parent wraps it in a line box whose baseline strut adds ~1.4px of
    // descender space (a fractional row height that re-rounds → 1px jitter on
    // re-render). items-start keeps the label content-width (not stretched).
    <div className={`flex flex-col items-start ${className}`}>
      <label
        className={`inline-flex items-start gap-2.5 min-h-6 min-w-6 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <span className="relative inline-flex shrink-0 mt-0.5">
          <input
            type="checkbox"
            id={id}
            name={name}
            checked={checked}
            disabled={disabled}
            aria-label={label ? undefined : ariaLabel}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy || undefined}
            onChange={(e) => onChange(e.target.checked)}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={
              'flex h-[1.15rem] w-[1.15rem] items-center justify-center rounded-md border transition-colors ' +
              'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--color-primary-ring)] ' +
              (checked
                ? `bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)] ${error ? 'border-[var(--color-danger)]' : 'border-[var(--color-primary-display)]'}`
                : `bg-[var(--color-bg-page)] ${error ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]'}`)
            }
          >
            {checked && <icons.check aria-hidden size={14} weight="bold" />}
          </span>
        </span>
        {(label || description) && (
          <span className="flex flex-col">
            {label && <span className="text-sm text-[var(--color-text-primary)]">{label}</span>}
            {description && (
              <span id={descId} className="text-xs text-[var(--color-text-secondary)]">
                {description}
              </span>
            )}
          </span>
        )}
      </label>
      {error && (
        <p id={errorId} className="mt-1 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
}
